<template>
  <div class="ModelSelect">
    <div v-if="modelSearching" class="loader loader-positioning" />
    <div class="searchForm">
      <form v-on:submit.prevent="getModels">
        <div id="searchFormInputWrapper">
          <input
            id="searchBox"
            v-model="searchKey"
            :placeholder="$mlGet('modelSelect-NameInput')"
            @input="getModels"
          />
          <div class="inputSubmit">
            <input
              id="searchSubmit"
              type="submit"
              :value="$mlGet('modelSelect-Find')"
            />
            <label for="searchSubmit"><i class="fas fa-search"></i></label>
          </div>
        </div>
      </form>
    </div>

    <div class="resultForm">
      <div class="resultTableWrapper">
        <table>
          <tr
            v-for="model in localModels"
            :key="model.fmId"
            @click="selectModel(model)"
            :class="{ clickedItem: model.active }"
          >
            <td>{{ model.make.name }}</td>
            <td>{{ model.name }}</td>
          </tr>
        </table>
      </div>
    </div>
    <input
      name="rmaSerialnumber"
      type="text"
      :placeholder="$mlGet('rmaInformation-serialnumber-placeholder')"
      v-model="$store.state.rma.serialnumber"
      id="input-serialnumber"
    />
    <div class="information-box">
      <span v-html="$mlGet('modelSelect-informationBox')"></span>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import * as Sentry from "@sentry/vue";
import { Model } from "@/types";
import {
  PageChangeContext,
  PageComponent,
  PageName
} from "@/views/CreateRma.vue";
import { highlightElementQuerySelector } from "@/helpers/alerts";

@Component
export default class ModelSelect extends Vue implements PageComponent {
  $refs!: {
    purchaseReceipt: HTMLFormElement;
  };
  public modelSearching = false;
  public localModels: Model[] = [];
  public searchKey = "";
  public base64Image = "";

  mounted() {
    if (!this.$store.state.rma.model.fmId) {
      const element = document.querySelector("#searchBox");

      if (element) {
        element.setAttribute("style", "border: 2px solid #da6f38");
        element.classList.add("animate__animated", "animate__flash");
        setTimeout(() => {
          element.setAttribute("style", "");
          element.classList.remove("animate__animated", "animate__flash");
        }, 3000);
      }
    }
    this.searchFromURL();
  }

  public async beforePageLeave(context: PageChangeContext): Promise<boolean> {
    if (!this.$store.state.rma.model.fmId) {
      highlightElementQuerySelector("#searchBox");
      return false;
    }

    if (
      this.$store.state.rma.serialnumber == "" &&
      context.hasVisitedNewPage === false
    ) {
      await this.$swal(
        "",
        this.$mlGet("error-serialnumber-missing"),
        "warning"
      );
    }

    return true;
  }

  public async afterPageEnter(context: PageChangeContext): Promise<void> {
    return;
  }

  public async onPageLeave(context: PageChangeContext): Promise<PageName> {
    if (this.$store.state.user.hasDealerSpecialOptions)
      return "DealerSpecialOptions";

    return "WarrantyInformation";
  }

  getModels(): void {
    this.modelSearching = true;

    if (this.searchKey.length < 2) {
      setTimeout(() => {
        this.modelSearching = false;
      }, 400);
      return;
    }

    this.$axios
      .get(`/models?search=${this.searchKey}`, {})
      .then(res => {
        this.localModels = res.data;
        setTimeout(() => {
          this.modelSearching = false;
        }, 400);
      })
      .catch(error => {
        console.error(error);
        this.$swal({
          title: this.$mlGet("modelSelect-searchError-head"),
          html: this.$mlGet("modelSelect-searchError-text")
        });

        Sentry.captureException(error, {
          extra: {
            searchKey: this.searchKey
          }
        });

        this.modelSearching = false;
      });
  }

  async selectModel(model: Model): Promise<void> {
    if (this.$store.state.rma.model) this.$store.commit("cleanRma");
    this.$store.state.rma.model = model;
    Vue.set(model, "graphic", await this.getGraphic(model));

    this.localModels.map(e => Vue.set(e, "active", false));
    Vue.set(model, "active", true);

    this.highlightElement("#input-serialnumber");
  }

  highlightElement(fieldId: string): void {
    const element: HTMLInputElement | null = document.querySelector(fieldId);
    if (!element) return;

    element.setAttribute("style", "border: 2px solid #da6f38");
    element.classList.add("animate__animated", "animate__flash");
    setTimeout(() => {
      element.setAttribute("style", "");
      element.classList.remove("animate__animated", "animate__flash");
    }, 1800);
  }

  async getGraphic(model: Model): Promise<string | void> {
    try {
      const response = await this.$axios.get(
        `/models/graphic?id=${model.fmId}`,
        {}
      );

      const formated = "data:image/jpeg;base64," + response?.data[0]?.graphic;
      return formated;
    } catch (error) {
      console.error(error);
    }
  }

  searchFromURL(): void {
    if (this.$route.query.search && !this.$store.state.rma.model.fmId) {
      this.searchKey = this.$route.query.search as string;
      this.getModels();
    }
  }
}
</script>

<style scoped>
.ModelSelect {
  min-height: auto;
  height: 100%;
}

.clickedItem {
  background-color: #f0bda6 !important;
}

#input-serialnumber {
  border: 1px solid lightgray;
  box-sizing: border-box;
  padding: 5px;
  margin: 5px 0;
  width: 100%;
}

.searchForm {
  text-align: left;
}

#searchFormInputWrapper {
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: auto min-content;
}

.searchForm input {
  background-color: #f2f3f2;
  border: 1px solid lightgray;
  padding: 2px 10px;
  border-radius: 2px;
  color: black;
}

.searchForm input::placeholder {
  color: rgb(40, 40, 40);
}

.inputSubmit {
  background-color: #da6f38 !important;
  border: none !important;
  padding: 3px 8px !important;
  margin: 0px 0px 0px 10px !important;
  color: white !important;
  cursor: pointer;
  font-size: 120%;
  display: flex;
  justify-content: center;
  gap: 3px;
}

.inputSubmit label {
  font-size: 100%;
  padding: 4px;
  margin-top: 2px;
  cursor: pointer;
}

.inputSubmit input {
  font-family: overpass, arial;
  background-color: #da6f38 !important;
  border: none !important;
  font-size: 120%;
  font-weight: 400;
  color: white;
  padding: 0;
  margin-top: 5px;
  cursor: pointer;
}

.resultForm {
  margin-top: 1ex;
  margin-right: 1ex;
  height: 30ex;
}

.resultTableWrapper {
  height: 90%;
  overflow: scroll;
  overflow-x: auto;
}

.resultTableWrapper::-webkit-scrollbar {
  width: 20px;
}

.resultTableWrapper::-webkit-scrollbar-track {
  background-color: lightgray;
}

.resultTableWrapper::-webkit-scrollbar-thumb {
  background-color: #da6f38;
}

.resultForm table {
  table-layout: fixed;
  text-align: left;
  width: 100%;
  border-collapse: collapse;
}

.resultForm table tr {
  border: 1px dotted lightgray;
  padding: 2px;
  margin: 2px;
}

.resultForm table tr:hover {
  background-color: #f7e2d8;
  cursor: pointer;
  transition: 0.3s;
}

td {
  padding: 5px 10px;
  border-bottom: 1px dotted lightgray;
}
</style>
